/**
 * KEJJA — Meals · Plan v2
 * Each slot holds multiple items (eggs + tea + potatoes) — not a single recipe.
 * Templates: save week, apply previous, copy.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMyHomes } from '@/hooks/useMe';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Plus, X, Loader2, Copy, BookmarkPlus,
  ListTodo, Sparkles, ShoppingCart, Trash2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'] as const;
type MealType = typeof MEAL_TYPES[number];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Cozy palette — warm tones, food-themed
const MEAL_THEME: Record<MealType, { emoji: string; tile: string; chip: string; label: string; accent: string }> = {
  breakfast: {
    emoji: '🌅',
    tile:  'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50',
    chip:  'bg-amber-100 dark:bg-amber-900/50 text-amber-900 dark:text-amber-200',
    label: 'text-amber-700 dark:text-amber-300',
    accent:'#f59e0b',
  },
  lunch: {
    emoji: '☀️',
    tile:  'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50',
    chip:  'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-900 dark:text-emerald-200',
    label: 'text-emerald-700 dark:text-emerald-300',
    accent:'#10b981',
  },
  dinner: {
    emoji: '🌙',
    tile:  'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900/50',
    chip:  'bg-rose-100 dark:bg-rose-900/50 text-rose-900 dark:text-rose-200',
    label: 'text-rose-700 dark:text-rose-300',
    accent:'#f43f5e',
  },
  snack: {
    emoji: '🍪',
    tile:  'bg-violet-50 dark:bg-violet-950/30 border-violet-200 dark:border-violet-900/50',
    chip:  'bg-violet-100 dark:bg-violet-900/50 text-violet-900 dark:text-violet-200',
    label: 'text-violet-700 dark:text-violet-300',
    accent:'#a855f7',
  },
};

interface Slot {
  id: string; homeId: string;
  weekStartDate: string; dayOfWeek: number; mealType: MealType;
  recipeId: string | null;
  mealText: string | null;
  notes: string | null;
}
interface SlotItem {
  id: string; slotId: string;
  recipeId: string | null; itemText: string | null;
  servings: number | null; sortOrder: number;
  recipeName: string | null; recipeServings: number | null;
}
interface Recipe { id: string; name: string; servings: number; }
interface Template { id: string; name: string; description: string | null; createdAt: string; }

function getMonday(d: Date): string {
  const x = new Date(d);
  const day = x.getDay() || 7;
  if (day !== 1) x.setHours(-24 * (day - 1));
  const y = x.getFullYear(), m = String(x.getMonth() + 1).padStart(2, '0'), dd = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function addWeeks(weekStart: string, n: number): string {
  const d = new Date(weekStart + 'T00:00:00');
  d.setDate(d.getDate() + n * 7);
  return getMonday(d);
}
function fmtWeekRange(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00');
  const end = new Date(start); end.setDate(end.getDate() + 6);
  const opt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', opt)} – ${end.toLocaleDateString('en-US', opt)}`;
}

export default function PlanPageV2() {
  const { data: homesData } = useMyHomes();
  const home = homesData?.homes?.[0]?.home;
  const homeId = home?.id;
  const qc = useQueryClient();
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [pickerSlot, setPickerSlot] = useState<{ slotId: string; day: number; meal: MealType } | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTpl, setShowSaveTpl] = useState(false);

  const slotsQ = useQuery({
    queryKey: ['meal-slots', homeId, weekStart],
    queryFn: () => api.get<{ slots: Slot[] }>(`/homes/${homeId}/meals/plan?week=${weekStart}`),
    enabled: !!homeId,
  });
  const itemsQ = useQuery({
    queryKey: ['meal-items', homeId, weekStart],
    queryFn: () => api.get<{ slotItems: Record<string, SlotItem[]> }>(`/homes/${homeId}/meals-v2/weeks/${weekStart}/items`),
    enabled: !!homeId,
  });
  const recipesQ = useQuery({
    queryKey: ['recipes', homeId],
    queryFn: () => api.get<{ recipes: Recipe[] }>(`/homes/${homeId}/meals/recipes`),
    enabled: !!homeId,
  });
  const yieldQ = useQuery({
    queryKey: ['yield', homeId],
    queryFn: () => api.get<{ servings: number }>(`/homes/${homeId}/meals-v2/recommended-yield`),
    enabled: !!homeId,
  });

  const slotByKey = useMemo(() => {
    const m = new Map<string, Slot>();
    (slotsQ.data?.slots ?? []).forEach(s => m.set(`${s.dayOfWeek}:${s.mealType}`, s));
    return m;
  }, [slotsQ.data]);
  const itemsBySlot = itemsQ.data?.slotItems ?? {};
  const recipes = recipesQ.data?.recipes ?? [];
  const homeYield = yieldQ.data?.servings ?? 4;

  const ensureSlot = useMutation({
    mutationFn: async (p: { day: number; meal: MealType }) => {
      const res = await api.post<{ slot: Slot }>(`/homes/${homeId}/meals/plan/slot`, {
        weekStartDate: weekStart, dayOfWeek: p.day, mealType: p.meal,
      });
      return res.slot;
    },
  });

  const addItemMut = useMutation({
    mutationFn: (p: { slotId: string; recipeId?: string | null; itemText?: string | null; servings?: number | null }) =>
      api.post(`/homes/${homeId}/meals-v2/slots/${p.slotId}/items`, p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meal-items', homeId, weekStart] });
      setPickerSlot(null);
    },
  });

  const removeItemMut = useMutation({
    mutationFn: (itemId: string) => api.delete(`/homes/${homeId}/meals-v2/slot-items/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meal-items', homeId, weekStart] }),
  });

  const clearWeek = useMutation({
    mutationFn: () => api.delete(`/homes/${homeId}/meals/plan?week=${weekStart}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meal-slots', homeId, weekStart] });
      qc.invalidateQueries({ queryKey: ['meal-items', homeId, weekStart] });
      toast.success('Week cleared');
    },
  });

  const generateShopping = useMutation({
    mutationFn: () => api.post<{ added: number; skippedInPantry: number; skippedByTerm: number }>(
      `/homes/${homeId}/shopping-pantry/generate`, { weekStartDate: weekStart }
    ),
    onSuccess: (data) => {
      toast.success(`Added ${data.added} items · skipped ${data.skippedInPantry} in pantry · ${data.skippedByTerm} staples`);
    },
    onError: (e: any) => toast.error(e?.message ?? 'Generate failed'),
  });

  const handleAddItem = async (day: number, meal: MealType) => {
    const key = `${day}:${meal}`;
    let slot = slotByKey.get(key);
    if (!slot) {
      slot = await ensureSlot.mutateAsync({ day, meal });
      qc.invalidateQueries({ queryKey: ['meal-slots', homeId, weekStart] });
    }
    setPickerSlot({ slotId: slot.id, day, meal });
  };

  if (!homeId) return null;

  return (
    <div className="space-y-5 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" onClick={() => setWeekStart(addWeeks(weekStart, -1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="font-semibold text-sm whitespace-nowrap">{fmtWeekRange(weekStart)}</div>
          <Button size="icon" variant="outline" onClick={() => setWeekStart(addWeeks(weekStart, 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setWeekStart(getMonday(new Date()))}>This week</Button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground hidden md:inline">Family of {homeYield}</span>
          <Button size="sm" variant="outline" onClick={() => {
            if (window.confirm('Clear all meals for this week?')) clearWeek.mutate();
          }} disabled={clearWeek.isPending}
            className="text-rose-600 border-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30">
            {clearWeek.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Clear week
          </Button>
          <Button size="sm" onClick={() => generateShopping.mutate()} disabled={generateShopping.isPending}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {generateShopping.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShoppingCart className="w-4 h-4 mr-1" />}
            Generate list
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowTemplates(true)}>
            <ListTodo className="w-4 h-4 mr-1" /> Templates
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowSaveTpl(true)}>
            <BookmarkPlus className="w-4 h-4 mr-1" /> Save week
          </Button>
        </div>
      </div>

      {/* Grid */}
      {slotsQ.isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4">
          <div className="grid gap-2 min-w-[700px]" style={{ gridTemplateColumns: '90px repeat(7, 1fr)' }}>
            <div></div>
            {DAYS.map((d, i) => (
              <div key={i} className="text-center text-xs font-mono uppercase text-muted-foreground py-1">{d}</div>
            ))}
            {MEAL_TYPES.map(meal => (
              <>
                <div key={`label-${meal}`} className={cn('flex items-center justify-end pr-2 gap-1.5 text-xs font-semibold uppercase tracking-wide', MEAL_THEME[meal].label)}>
                  <span className="text-base">{MEAL_THEME[meal].emoji}</span>{meal}
                </div>
                {DAYS.map((_, day) => {
                  const slot = slotByKey.get(`${day}:${meal}`);
                  const items = slot ? (itemsBySlot[slot.id] ?? []) : [];
                  const theme = MEAL_THEME[meal];
                  return (
                    <div key={`${day}-${meal}`}
                         className={cn('min-h-[88px] p-2 space-y-1 rounded-xl border-2 transition-all hover:shadow-md', theme.tile)}>
                      {items.map(it => (
                        <div key={it.id} className={cn('group flex items-center gap-1 text-[11px] rounded-lg px-2 py-1', theme.chip)}>
                          <span className="flex-1 truncate font-medium" title={it.recipeName ?? it.itemText ?? ''}>
                            {it.recipeName ?? it.itemText}
                          </span>
                          <button onClick={() => removeItemMut.mutate(it.id)}
                                  className="opacity-0 group-hover:opacity-100 hover:text-destructive">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => handleAddItem(day, meal)}
                              className={cn('w-full text-[11px] py-1.5 rounded-lg hover:bg-white/40 dark:hover:bg-white/5 flex items-center justify-center gap-1', theme.label)}>
                        <Plus className="w-3 h-3" /> add
                      </button>
                    </div>
                  );
                })}
              </>
            ))}
          </div>
        </div>
      )}

      {/* Picker modal */}
      {pickerSlot && (
        <ItemPickerModal
          recipes={recipes}
          defaultServings={homeYield}
          onPick={(payload) => addItemMut.mutate({ slotId: pickerSlot.slotId, ...payload })}
          onClose={() => setPickerSlot(null)}
          busy={addItemMut.isPending}
        />
      )}

      {/* Templates modal */}
      {showTemplates && <TemplatesModal homeId={homeId} weekStart={weekStart} onClose={() => setShowTemplates(false)} />}

      {/* Save week modal */}
      {showSaveTpl && <SaveTemplateModal homeId={homeId} weekStart={weekStart} onClose={() => setShowSaveTpl(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------

function ItemPickerModal({ recipes, defaultServings, onPick, onClose, busy }: {
  recipes: Recipe[]; defaultServings: number;
  onPick: (p: { recipeId?: string | null; itemText?: string | null; servings?: number | null }) => void;
  onClose: () => void; busy: boolean;
}) {
  const [search, setSearch] = useState('');
  const [freeText, setFreeText] = useState('');
  const filtered = recipes.filter(r => r.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-semibold text-sm">Add to slot</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 space-y-3 overflow-y-auto flex-1">
          <div>
            <Input placeholder="Search recipes…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {filtered.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Recipes</div>
              {filtered.slice(0, 12).map(r => (
                <button key={r.id}
                        onClick={() => onPick({ recipeId: r.id, servings: defaultServings })}
                        disabled={busy}
                        className="w-full text-left px-3 py-2 rounded hover:bg-muted text-sm flex items-center justify-between">
                  <span>{r.name}</span>
                  <span className="text-[10px] text-muted-foreground">→ {defaultServings} servings</span>
                </button>
              ))}
            </div>
          )}
          <div className="border-t pt-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-1">Or just type it</div>
            <div className="flex gap-2">
              <Input placeholder="e.g. ugali + sukuma" value={freeText} onChange={e => setFreeText(e.target.value)} />
              <Button onClick={() => freeText && onPick({ itemText: freeText })} disabled={busy || !freeText}>
                Add
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplatesModal({ homeId, weekStart, onClose }: { homeId: string; weekStart: string; onClose: () => void }) {
  const qc = useQueryClient();
  const tplQ = useQuery({
    queryKey: ['templates', homeId],
    queryFn: () => api.get<{ templates: Template[] }>(`/homes/${homeId}/meals-v2/templates`),
  });
  const applyMut = useMutation({
    mutationFn: (p: { templateId: string; replace: boolean }) =>
      api.post(`/homes/${homeId}/meals-v2/templates/apply`, { ...p, weekStartDate: weekStart }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meal-slots', homeId, weekStart] });
      qc.invalidateQueries({ queryKey: ['meal-items', homeId, weekStart] });
      toast.success('Template applied');
      onClose();
    },
  });
  const delMut = useMutation({
    mutationFn: (id: string) => api.delete(`/homes/${homeId}/meals-v2/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates', homeId] }),
  });
  const copyMut = useMutation({
    mutationFn: () => api.post(`/homes/${homeId}/meals-v2/weeks/copy`, { from: addWeeks(weekStart, -1), to: weekStart }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meal-slots', homeId, weekStart] });
      qc.invalidateQueries({ queryKey: ['meal-items', homeId, weekStart] });
      toast.success('Last week copied to this week');
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-semibold text-sm flex items-center gap-2"><ListTodo className="w-4 h-4" /> Meal templates</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 space-y-2 overflow-y-auto flex-1">
          <Button variant="outline" className="w-full" onClick={() => copyMut.mutate()} disabled={copyMut.isPending}>
            <Copy className="w-4 h-4 mr-2" /> Copy last week to this week
          </Button>
          {tplQ.isLoading ? (
            <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
          ) : (tplQ.data?.templates ?? []).length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">No saved templates yet. Use "Save week" to create one.</div>
          ) : (
            <div className="space-y-1.5 pt-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">Saved templates</div>
              {(tplQ.data?.templates ?? []).map(t => (
                <Card key={t.id}>
                  <CardContent className="p-2.5 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{t.name}</div>
                      {t.description && <div className="text-[11px] text-muted-foreground truncate">{t.description}</div>}
                    </div>
                    <Button size="sm" variant="outline"
                            onClick={() => applyMut.mutate({ templateId: t.id, replace: false })}
                            disabled={applyMut.isPending}>
                      Apply
                    </Button>
                    <button onClick={() => delMut.mutate(t.id)}
                            className="text-muted-foreground hover:text-destructive">
                      <X className="w-4 h-4" />
                    </button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SaveTemplateModal({ homeId, weekStart, onClose }: { homeId: string; weekStart: string; onClose: () => void }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const qc = useQueryClient();
  const saveMut = useMutation({
    mutationFn: () => api.post(`/homes/${homeId}/meals-v2/templates`, { name, description: desc || undefined, weekStartDate: weekStart }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates', homeId] });
      toast.success(`"${name}" saved`);
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="font-semibold text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Save this week</div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          <Input placeholder="Template name (e.g. School week)" value={name} onChange={e => setName(e.target.value)} />
          <Input placeholder="Description (optional)" value={desc} onChange={e => setDesc(e.target.value)} />
          <Button onClick={() => name && saveMut.mutate()} disabled={!name || saveMut.isPending} className="w-full">
            {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save template'}
          </Button>
        </div>
      </div>
    </div>
  );
}
